/**
 * `@simplewebauthn/browser` implementation of PasskeyProvider (CU-86ajzr8r0).
 *
 * THIS IS THE ONLY FILE IN THE APP THAT IMPORTS THE PASSKEY LIBRARY
 * (AUTH_STRATEGY invariant #3 — keep the dependency shallow and swappable).
 * The admin console's `webPasskeyProvider.ts` and the mobile analogue
 * `f23PasskeyProvider.ts` both state the same rule; it holds here for the same
 * reason and should be preserved.
 *
 * ⚠️ WHY A LIBRARY AND NOT `navigator.credentials` DIRECTLY. Every WebAuthn
 * binary field crosses the wire as base64url and reaches the browser API as an
 * ArrayBuffer. Hand-rolling that conversion is the single largest source of
 * bugs in browser WebAuthn code. `startRegistration` / `startAuthentication`
 * do the conversion in both directions and emit exactly
 * `RegistrationResponseJSON` / `AuthenticationResponseJSON` — which is what
 * `PasskeyAttestationResponse` / `PasskeyAssertionResponse` in mideo-types
 * mirror. So the ceremony output is POSTed to the server with no massaging.
 *
 * VERSION MATCH: browser 13.3.0 against the server's `@simplewebauthn/server`
 * ^13.3.2 — same major line. The two packages version independently; matching
 * the major is what keeps the JSON shapes compatible.
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type { PasskeyAttestationResponse, PasskeyAssertionResponse } from '@usacsmideo/types';
import type {
  PasskeyProvider,
  PasskeyRegisterOptions,
  PasskeyRegisterResult,
  PasskeyAuthenticateOptions,
  PasskeyAuthenticateResult,
  PasskeyFailureReason,
} from './PasskeyProvider';

/** ES256 (-7) then RS256 (-257) — mirrors the server's pinned allowlist. */
const PUB_KEY_CRED_PARAMS = [
  { type: 'public-key' as const, alg: -7 },
  { type: 'public-key' as const, alg: -257 },
];

/** The RP display name shown in the browser's passkey sheet. */
const RP_NAME = 'MIDEO';

/**
 * Map a ceremony error to our coarse reason.
 *
 * ⚠️ WHY `error.name` IS THE RIGHT KEY, RE-VERIFIED AGAINST THE VERSION
 * INSTALLED HERE (13.3.0) RATHER THAN INHERITED FROM THE CONSOLE'S COMMENT.
 * v13 does not rethrow the raw DOMException — it wraps it in its own
 * `WebAuthnError`. But that wrapper's constructor does
 * `this.name = name ?? cause.name` (esm/helpers/webAuthnError.js:28), i.e. it
 * PRESERVES the original DOMException name unless one is explicitly overridden.
 * All 17 construction sites across `identifyRegistrationError.js` (12) and
 * `identifyAuthenticationError.js` (5) pass only `message`, `code` and `cause`
 * — none passes `name` (checked by grep on the installed build, 2026-08-12).
 * So `.name` carries the spec error identically whether the error is wrapped or
 * raw — which also means this mapping keeps working if the library is swapped
 * for bare `navigator.credentials`, since that is the raw DOMException contract.
 *
 * `NotAllowedError` → `cancelled` COVERS BOTH USER DISMISSAL AND TIMEOUT, and
 * that is not a shortcut: the spec deliberately collapses them so a site cannot
 * distinguish "user refused" from "user has no such credential" from "the
 * ceremony timed out". The library's own comment on this branch says platforms
 * overload the error "beyond what the spec defines", which is why it passes the
 * message through untouched (code `ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY`)
 * instead of interpreting it. We record the detail for Sentry and do the same.
 *
 * ⚠️ DO NOT add a heuristic here that tries to recover "empty credential store"
 * from the message text. The mobile codebase tried exactly that (CU-86ajw3hv9)
 * against iOS and MEASURED that the information is not present. On the web the
 * supported answer is conditional mediation (sub-task 3), not string-sniffing.
 */
function toFailureReason(
  error: unknown,
  ceremony: 'register' | 'authenticate',
): { reason: PasskeyFailureReason; detail?: string } {
  const name = (error as { name?: string })?.name;
  const detail = (error as { message?: string })?.message ?? name;

  switch (name) {
    case 'NotAllowedError':
      // User dismissed the sheet, or the ceremony timed out. Indistinguishable
      // by design — see the note above.
      return { reason: 'cancelled', detail };
    case 'AbortError':
      // An AbortController cancelled the ceremony. In sub-task 3 this is the
      // ROUTINE outcome of a conditional (autofill) request being superseded by
      // a modal one, so it must stay benign rather than reading as a fault.
      return { reason: 'cancelled', detail };
    case 'InvalidStateError':
      // ON CREATE ONLY this means "this authenticator already holds a
      // credential for this RP" — i.e. `excludeCredentials` matched. That is a
      // legitimate, actionable outcome, not a failure.
      //
      // On GET the same name means something else entirely, so it must NOT map
      // to already-registered there.
      return ceremony === 'register' ? { reason: 'already-registered', detail } : { reason: 'failed', detail };
    case 'NotSupportedError':
      return { reason: 'not-supported', detail };
    default:
      return { reason: 'failed', detail };
  }
}

export const webPasskeyProvider: PasskeyProvider = {
  isSupported(): boolean {
    try {
      return browserSupportsWebAuthn();
    } catch {
      // A throwing probe means unusable, not fatal — the password path is
      // always available, so degrade quietly. (Mirrors the mobile provider's
      // treatment of a throwing native bridge.)
      return false;
    }
  },

  async supportsAutofill(): Promise<boolean> {
    try {
      return await browserSupportsWebAuthnAutofill();
    } catch {
      // Same posture as `isSupported()`: a throwing probe means "do not offer
      // autofill", not a fatal condition. The visible button and the password
      // path both remain.
      return false;
    }
  },

  cancelCeremony(): void {
    try {
      WebAuthnAbortService.cancelCeremony();
    } catch {
      // Teardown must never throw — this runs from an unmount cleanup, where a
      // throw would surface as a React error with nothing actionable behind it.
    }
  },

  async register(opts: PasskeyRegisterOptions): Promise<PasskeyRegisterResult> {
    try {
      const attestation = await startRegistration({
        optionsJSON: {
          challenge: opts.challenge,
          rp: { id: opts.rpId, name: RP_NAME },
          user: {
            // WebAuthn requires the user handle as a byte string; the JSON
            // form takes it base64url-encoded, and the library decodes it.
            id: encodeUserId(opts.userId),
            name: opts.userName,
            displayName: opts.userDisplayName,
          },
          pubKeyCredParams: PUB_KEY_CRED_PARAMS,
          timeout: opts.timeoutMs,
          // Map to the library's descriptor shape at the boundary — `type` +
          // `id` only. Our wire type carries `transports` as plain strings
          // (correct for a cross-repo contract) while the library wants its own
          // union, and transports is an optional HINT this list does not need.
          // Dropping it keeps the exclusion behaviour identical.
          ...(opts.excludeCredentials?.length
            ? {
                excludeCredentials: opts.excludeCredentials.map((c) => ({
                  type: 'public-key' as const,
                  id: c.id,
                })),
              }
            : {}),
          authenticatorSelection: {
            // ⚠️ DELIBERATELY NOT `authenticatorAttachment: "platform"`, which
            // the mobile provider pins. On mobile the platform authenticator is
            // the only sensible target; in a browser, pinning it would exclude
            // roaming security keys (YubiKey et al.) — and patients are far more
            // device-diverse than the console's three admins, so the argument
            // for leaving this open is stronger here, not weaker.
            //
            // UV-REQ (client half): demand a real user verification, not a bare
            // presence tap. The server independently asserts the `uv` flag;
            // both are required for the AAL2 claim to hold.
            userVerification: 'required',
            // Discoverable credential — the sub-task 3 login ceremony must work
            // with no prior account context (the patient is logged out), and
            // conditional mediation requires it.
            residentKey: 'required',
          },
          // Synced passkeys are accepted, so we do not need attestation to
          // distinguish authenticator provenance. Consequence: AAGUID is not
          // reliably readable, and no policy may key on it.
          attestation: 'none',
        },
      });
      return {
        ok: true,
        attestation: attestation as PasskeyAttestationResponse,
      };
    } catch (error) {
      return { ok: false, ...toFailureReason(error, 'register') };
    }
  },

  async authenticate(opts: PasskeyAuthenticateOptions): Promise<PasskeyAuthenticateResult> {
    try {
      const assertion = await startAuthentication({
        optionsJSON: {
          challenge: opts.challenge,
          rpId: opts.rpId,
          timeout: opts.timeoutMs,
          // UV-REQ (client half) — see register().
          userVerification: 'required',
          // NO `allowCredentials`: the login ceremony runs logged-out, so we
          // have no account context to build a list from. This is what makes
          // the credential discoverable-only, and what sub-task 3's conditional
          // mediation builds on. (In conditional mode the library forces
          // `allowCredentials: []` itself, so the two agree.)
        },
        // Sub-task 3 (CU-86ajzr94n) — conditional mediation (autofill).
        //
        // Setting this makes the library request `mediation: "conditional"`,
        // which surfaces credentials in the browser's autofill popup instead of
        // a modal sheet. Absent/false keeps the modal behaviour byte-for-byte,
        // which is what the visible button uses.
        useBrowserAutofill: opts.conditional === true,
      });
      return { ok: true, assertion: assertion as PasskeyAssertionResponse };
    } catch (error) {
      return { ok: false, ...toFailureReason(error, 'authenticate') };
    }
  },
};

/**
 * base64url-encode the user handle.
 *
 * Unlike the mobile provider — which hand-rolls this because Hermes lacks
 * `Buffer` (MIDEO-UNIFIED-7Y) — the browser has `TextEncoder` and `btoa`
 * universally, so this is six lines rather than sixty. Exported for the tests
 * that pin the encoding.
 */
export function encodeUserId(uid: string): string {
  const bytes = new TextEncoder().encode(uid);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
